// The "Set up LED" dialog. The logic is in ledwizard.js; this file only draws it.
import { LedWizard } from './ledwizard.js';
import { LED_PINS } from './device.js';
import { button, paragraph, openWizardDialog } from './wizard_dialog.js';

// getDevice(): the current DeviceLink. reconnect(): waits for the ESP to come back
// after its restart and reopens the serial port; resolves true when it worked.
export function openLedWizard({ getDevice, reconnect }) {
  let wizard = null;
  let finished = false;

  const close = async () => {
    const endsTest = !finished && wizard?.testing;
    if (!finished && wizard) await wizard.cancel(); // ends a running test: the ESP restarts
    finished = true;
    dlg.close();
    if (endsTest) reconnect(); // the restart dropped the connection: come back by ourselves
  };
  const dlg = openWizardDialog({ onRequestClose: close });
  const show = dlg.show;

  const yesNo = (w, yes, no) => [
    button('Can’t tell, show again', () => w.answer('unsure'), 'outline secondary'),
    button(no, () => w.answer('no'), 'secondary'),
    button(yes, () => w.answer('yes')),
  ];

  // The pins that are already in use (button, bus lines) are never driven by the test, so with the
  // button and the bus pins set up the serial cable can stay connected. Only when one of them is
  // missing (the LED was asked for too early) the old precautions apply.
  const intro = async () => {
    let config = null;
    try {
      config = (await getDevice().getConfig())?.config ?? null;
    } catch {
      // unknown: treated like "not set up", the warning below then applies
    }
    const busDone = config?.dat != null && config?.clk != null && config?.atn != null;
    const buttonDone = !!config?.buttonDone;
    const inUse = config ? [config.button, config.dat, config.clk, config.atn, config.rst].filter((p) => p != null) : [];
    const candidates = LED_PINS.filter((p) => !inUse.includes(p));
    const missing = [...(buttonDone ? [] : ['the button']), ...(busDone ? [] : ['the bus pins'])];
    const early = missing.length > 0;

    const check = Object.assign(document.createElement('input'), { type: 'checkbox', id: 'led-unplugged' });
    const label = document.createElement('label');
    label.append(check, ' I have unplugged the serial cable from the C64');
    const startWith = (count) => async () => {
      wizard = new LedWizard(getDevice(), { candidates, count, onChange: render });
      try {
        await wizard.start();
      } catch (e) {
        render({ step: 'failed', error: e.message, retryable: false });
      }
    };
    const one = button('One LED', startWith(1));
    const two = button('Two LEDs', startWith(2));
    if (early) {
      one.disabled = two.disabled = true;
      check.onchange = () => (one.disabled = two.disabled = !check.checked);
    }
    show(
      'Set up the status LEDs',
      [
        paragraph('How many status LEDs does your device have? <strong>One</strong> LED shows the printer (activity, a full buffer) and, in Wi-Fi mode, the connection state. With <strong>two</strong> LEDs the first shows the printer only and the second the Wi-Fi state: off = Wi-Fi printing is not used, on = Wi-Fi printing is on, slow blinking = Wi-Fi activity, fast blinking = a Wi-Fi, network or server error.'),
        paragraph('This finds out which pins the LEDs are connected to. The ESP will switch many of its pins on and off. Keep your LED in view and ignore any other LED, for example the one on the board itself, unless that is the one you want to use.'),
        ...(early
          ? [
              paragraph(`<strong>You have not set up ${missing.join(' and ')} yet.</strong> Do that first (Set up, then ${missing.map((m) => (m === 'the button' ? 'Button' : 'Bus pins')).join(' and ')}): then this test skips those pins and the serial cable can stay connected. If you carry on anyway, <strong>unplug the serial cable from the C64 first</strong> (connected to the bus, the pin test could damage the C64 or the ESP)${buttonDone ? '' : ' and do not touch the button while the test runs'}.`),
              label,
            ]
          : []),
      ],
      [button('Cancel', close, 'outline secondary'), button('No LED', noLed, 'secondary'), one, two],
    );
  };

  const noLed = async () => {
    wizard = new LedWizard(getDevice(), { onChange: render });
    await save(wizard, 'No LED is stored.', () => wizard.saveNoLed());
  };

  // "your LED" with one LED, "your print LED" / "your Wi-Fi LED" with two
  const which = (w) => (w.ledName ? `${w.ledName} LED` : 'LED');
  const polarityText = (l) => `lights when the pin is <strong>${l.activeLow ? 'low' : 'high'}</strong>`;

  const render = (w) => {
    if (w.step === 'round') {
      show(
        `Is your ${which(w)} blinking? (step ${w.roundNumber} of ${w.totalRounds})`,
        [
          ...(w.ledName ? [paragraph(`Watch only the <strong>${w.ledName} LED</strong> now${w.slot === 0 ? ' (the one that shows the printer)' : ' (the one that shows the Wi-Fi state)'}.`)] : []),
          paragraph(`The ESP is blinking some of its pins about once a second. Wait a few seconds and watch your ${which(w)}.`),
          paragraph(`<small>${w.remainingCount} possible pins left.</small>`),
        ],
        yesNo(w, 'Yes, it blinks', 'No, it stays dark or steady'),
      );
    } else if (w.step === 'verify') {
      show(
        `Check (GPIO${w.pin})`,
        [paragraph(`One pin is left: <strong>GPIO${w.pin}</strong>. Only this pin is blinking now. Is your ${which(w)} blinking?`)],
        yesNo(w, 'Yes, it blinks', 'No'),
      );
    } else if (w.step === 'polarity') {
      show(
        `Is the ${which(w)} on?`,
        [paragraph(`GPIO${w.pin} is now held high, without blinking. Is your ${which(w)} lit?`)],
        yesNo(w, 'Yes, it is lit', 'No, it is dark'),
      );
    } else if (w.step === 'nextled') {
      show(
        'Print LED found',
        [
          paragraph(`Your print LED is on <strong>GPIO${w.leds[0].pin}</strong> and ${polarityText(w.leds[0])}.`),
          paragraph('Next is the <strong>Wi-Fi LED</strong>: the one that shows the Wi-Fi state. All pins are dark now; the search starts when you press Next, and then you watch only that second LED.'),
        ],
        [button('Cancel', close, 'outline secondary'), button('Next', () => w.next())],
      );
    } else if (w.step === 'ready') {
      show(
        w.leds.length === 1 ? 'LED found' : 'LEDs found',
        [
          ...(w.leds.length === 1
            ? [paragraph(`Your LED is on <strong>GPIO${w.leds[0].pin}</strong> and ${polarityText(w.leds[0])}.`)]
            : [
                paragraph(`Your print LED is on <strong>GPIO${w.leds[0].pin}</strong> and ${polarityText(w.leds[0])}.`),
                paragraph(`Your Wi-Fi LED is on <strong>GPIO${w.leds[1].pin}</strong> and ${polarityText(w.leds[1])}.`),
              ]),
          paragraph(
            w.leds.length === 1
              ? 'Saving stores this in the device and restarts it. The LED then blinks three times at power-up and shows printer activity and the Wi-Fi state.'
              : 'Saving stores this in the device and restarts it. Both LEDs then blink three times at power-up. The print LED shows printer activity, the Wi-Fi LED the Wi-Fi state.',
          ),
        ],
        [button('Cancel', close, 'outline secondary'), button('Save to the device', () => save(w))],
      );
    } else if (w.step === 'saving') {
      show('Saving…', [paragraph('Storing the setting in the device.')], []);
    } else if (w.step === 'failed') {
      show(
        'That did not work',
        [paragraph(w.error ?? 'Something went wrong.')],
        [button('Close', close, 'outline secondary'), ...(w.retryable === false ? [] : [button('Try again', () => w.retry())])],
      );
    }
  };

  const save = async (w, doneText = w.leds.length === 2
    ? `The print LED (GPIO${w.leds[0].pin}) and the Wi-Fi LED (GPIO${w.leds[1].pin}) are set up. Both blink three times when the device powers up.`
    : `The LED on GPIO${w.pin} is set up. It blinks three times when the device powers up and shows printer activity and the Wi-Fi state.`, doSave = () => w.save()) => {
    try {
      await doSave();
    } catch {
      return; // the wizard shows the failure itself
    }
    finished = true; // the restart ends the test, nothing left to cancel
    show('Restarting the device…', [paragraph('The device restarts to use the new setting. The page reconnects by itself.')], []);
    const ok = await reconnect();
    if (ok) {
      show(
        'Done',
        [paragraph(doneText)],
        [button('Close', close)],
      );
    } else {
      show(
        'Saved, but the page could not reconnect',
        [paragraph('The setting is stored. Replug the device if needed and press Connect.')],
        [button('Close', close)],
      );
    }
  };

  intro();
}
